import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FormInput, Modal } from '@/components/common';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { useAuthStore } from '@/stores/authStore';
import { setMobilePinService } from './services';
import { type IMobilePinForm, mobilePinSchema } from './utils';

interface MobilePinModalProps {
  onClose: () => void;
  onSaved: () => void;
}

// Profile menu → Mobile PIN: set or change your own resident-app PIN. It is also the only
// way to reset a forgotten one, since the app's forgot-password refuses staff numbers.
export function MobilePinModal({ onClose, onSaved }: MobilePinModalProps) {
  const admin = useAuthStore((state) => state.admin);
  const signIn = useAuthStore((state) => state.signIn);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: setMobilePinService,
    // Your row on Administrators (PIN not set → On) and any open Mobile access dialog.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.administrators }),
  });
  const { control, handleSubmit } = useForm<IMobilePinForm>({
    resolver: zodResolver(mobilePinSchema),
    defaultValues: { pin: '', pin_confirmation: '', current_password: '' },
  });

  const submit = handleSubmit((values) =>
    save.mutate(values, {
      onSuccess: ({ admin: saved }) => {
        signIn(saved);
        onSaved();
      },
    }),
  );

  return (
    <Modal
      title="Mobile PIN"
      subtitle={`For the resident app with ${admin?.mobile ?? 'your mobile'}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="submit"
            form="mobile-pin"
            disabled={save.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
          >
            {save.isPending ? 'Saving…' : 'Save PIN'}
          </button>
          <button type="button" onClick={onClose} className="text-sm text-gray-600 hover:underline">
            Cancel
          </button>
        </>
      }
    >
      <form id="mobile-pin" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput
            control={control}
            name="pin"
            label="New PIN"
            isSensitive
            isRequired
            digits={6}
          />
          <FormInput
            control={control}
            name="pin_confirmation"
            label="Type it again"
            isSensitive
            isRequired
            digits={6}
          />
        </div>
        <FormInput
          control={control}
          name="current_password"
          label="Your console password"
          isSensitive
          isRequired
        />
        <p className="text-xs text-gray-500">
          A new PIN also clears a lock after wrong PINs. Phones already signed in stay signed in.
        </p>
        {save.error && (
          <p className="text-sm text-red-600" role="alert">
            {save.error.message}
          </p>
        )}
      </form>
    </Modal>
  );
}
